import { useState, type ReactNode } from 'react';
import { useT } from '../i18n/index.ts';

interface Props {
  readonly summary: string;
  readonly children: ReactNode;
  readonly testId: string;
  readonly compact?: boolean;
  readonly title?: string;
}

export function TechnicalDetails({
  summary,
  children,
  testId,
  compact = false,
  title,
}: Props): JSX.Element {
  const t = useT();
  const [open, setOpen] = useState(false);

  return (
    <section
      className={[
        'rounded-xl bg-surface-container-low',
        compact ? 'p-3' : 'p-4',
      ].join(' ')}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline">
            {title ?? t('common.technical_details.title')}
          </h3>
          <p
            className={[
              'mt-1 text-outline',
              compact ? 'text-[11px] leading-5' : 'text-xs leading-5',
            ].join(' ')}
          >
            {summary}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen((current) => !current)}
          className="inline-flex shrink-0 items-center justify-center rounded-lg bg-surface-container-highest px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface transition-colors hover:bg-surface-bright"
          data-testid={`${testId}-toggle`}
        >
          {open ? t('common.technical_details.hide') : t('common.technical_details.show')}
        </button>
      </div>
      {open ? (
        <div
          className={[
            'mt-4 border-t border-outline-variant/40 pt-4',
            compact ? 'text-xs' : 'text-sm',
          ].join(' ')}
          data-testid={`${testId}-content`}
        >
          {children}
        </div>
      ) : null}
    </section>
  );
}
